import type { ReactElement } from "react"
import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useNavigate, useParams } from "react-router-dom"

import * as Yup from "yup"
import CloseIcon from "@mui/icons-material/Close"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Button, NotificationUtils, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik, useFormikContext } from "formik"

import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type {
  CreateAutoMlRequest,
  CustomFeatureRequest,
  TrainModelUsingPrebuiltModelRequest,
  baseErrorType,
} from "../../../types/custom/projects"
import type { ExcludedFeatureRequest } from "../../../types/generated/api/ExcludedFeatureRequest"
import type { TrainingDataSchemaResponse } from "../../../types/generated/api/TrainingDataSchemaResponse"
import { mapResponseTypesToTabValues } from "../../../utils/deploymentDetailsHelpers"
import { DataMappingFlow } from "../../projects/components/DataMappingFlow"
import { UploadTrainingDataFlow } from "../../projects/components/UploadTrainingDataFlow"
import type {
  AutoMLCreationDialogContainerProps,
  AutoMlCreationFormikValues,
  AutoMlModelCreationDialogProps,
} from "../interfaces"

/**
 * Dialog Component that handles the uploading and data mapping steps in Credit scoring projects.
 *
 * @param {boolean} open - Dialog open state.
 * @param {Function} setDialog - Setter function for handling dialog open/close.
 * @param {string} projectId - Current project ID.
 * @param {string | undefined} errorMessage - Error message string.
 * @param {Function} handleTrainClick - Handler function to toggle between different flow based on CS model type.
 * @param {Function} setTrainingDataId - Setter function for setting CS data.
 * @param {Function} setAutoMlData - Setter function for setting AutoML specific data.
 * @param {string} trainingDataId - Training data ID.
 * @param {AxiosError} automlErrors - Errors related to AutoML operations.
 * @returns {ReactElement} - The rendered dialog component.
 */
export function AutoMlModelCreationDialog(props: Readonly<AutoMlModelCreationDialogProps>): ReactElement {
  // Destructuring props for easier access
  const {
    open,
    errorMessage,
    setDialog,
    projectId,
    isBtnLoading,
    handleTrainClick,
    setTrainingDataId,
    setAutoMlData,
    trainingDataId,
    automlErrors,
  } = props

  const { values } = useFormikContext<AutoMlCreationFormikValues>()

  const errorALertRef = useRef<HTMLElement>(null)

  const theme = useTheme()
  const themeMode = getTheme()
  const { t } = useTranslation("models")

  // State to toggle between pre-built and AutoML model types
  const [selectedCSType, _] = useState<"auto-ml" | "pre-built">("auto-ml")

  // State to track if the next button has been clicked
  const [isNextButtonClicked, setIsNextButtonClicked] = useState<boolean>(false)

  // Fetch uploaded training data headers (columns)
  const { data: trainingSchema, isLoading: isLoadingTrainingSchema } = useQuery<TrainingDataSchemaResponse, AxiosError>(
    ["training-data-schema", trainingDataId],
    () => KonanAPI.fetchTrainingDataSchema(projectId, trainingDataId as string),
    {
      enabled: !!trainingDataId,
    },
  )

  // Scroll to the top of the dialog where the snackbar error should be displayed using errorALertRef
  useEffect(() => {
    if (errorMessage) {
      errorALertRef?.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [errorMessage])

  return (
    <Dialog
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && isBtnLoading) {
          return undefined
        } else {
          setDialog(false)
        }
      }}
      open={open}
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "800px",
        },
      }}
      fullScreen={useMediaQuery(theme.breakpoints.down("md"))}
    >
      <DialogTitle className="dialog-header-base">
        <Grid item container justifyContent="space-between" xs={12}>
          <Grid item alignSelf={"center"}>
            <Typography variant="h2-bold">{t("dialogs.creation.title")}</Typography>
          </Grid>

          <Grid item alignSelf={"center"}>
            <IconButton aria-label="close" onClick={() => setDialog(false)} className="close-icon-button">
              <CloseIcon style={{ color: themeMode.palette.gray.background[1] }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent className="dialog-content-base">
        {errorMessage && <Snackbar severity="error" fullWidth description={errorMessage} />}

        {isNextButtonClicked ? (
          <DataMappingFlow
            onDataChange={(mapping: Array<CustomFeatureRequest>, excludedColumns: Array<ExcludedFeatureRequest>) => {
              setAutoMlData({ mapping, excludedColumns })
            }}
            createdProjectId={projectId}
            fileHeaders={trainingSchema?.columns}
            errors={automlErrors}
            isSchemaLoading={isLoadingTrainingSchema}
            trainingDataId={trainingDataId as string}
          />
        ) : (
          <UploadTrainingDataFlow
            currentProjectId={projectId}
            type={"dialog"}
            onDataChange={(datasetId: string) => {
              setTrainingDataId(datasetId)
            }}
            creditScoringType={selectedCSType}
          />
        )}
      </DialogContent>

      <DialogActions className="dialog-actions-base">
        <Grid direction="column" container alignItems="flex-end" item xs={12}>
          <Grid item xs={12}>
            <Button
              variant="primary"
              onClick={() =>
                selectedCSType === "auto-ml" && !isNextButtonClicked
                  ? setIsNextButtonClicked(true)
                  : selectedCSType === "auto-ml" && isNextButtonClicked
                    ? handleTrainClick("auto-ml")
                    : handleTrainClick("pre-built")
              }
              disabled={
                selectedCSType === "auto-ml" && !isNextButtonClicked
                  ? !values.name || !trainingDataId
                  : selectedCSType === "auto-ml" && isNextButtonClicked
                    ? !values.target_column
                    : isBtnLoading
              }
              isLoading={isBtnLoading}
            >
              {selectedCSType === "auto-ml" && !isNextButtonClicked
                ? t("dialogs.automl_creation.next")
                : selectedCSType === "auto-ml" && isNextButtonClicked && t("dialogs.automl_creation.train")}
            </Button>
          </Grid>
          {(selectedCSType === "pre-built" || (selectedCSType === "auto-ml" && isNextButtonClicked)) && (
            <Typography variant="span-bold" color="base" variantColor={2} disabled style={{ marginTop: "5px" }}>
              {t("dialogs.automl_creation.redirect_note")}
            </Typography>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

// Form validation schema for AutoML training
const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  target_column: Yup.string().required("Required"),
  targetColumnType: Yup.string(),
})

/**
 * Container component for handling training data upload functionality.
 * Manages dialog state and training mutations for both AutoML and pre-built models.
 *
 * @param {AutoMLCreationDialogContainerProps} props - Component properties.
 * @param {boolean} isOpen - Dialog visibility state.
 * @param {Function} setIsOpen - Dialog state setter.
 * @returns {React.ReactElement} - The rendered container component.
 */
export function AutoMLCreationDialogContainer(props: Readonly<AutoMLCreationDialogContainerProps>): ReactElement {
  const { isOpen, setIsOpen } = props
  const { id: projectId } = useParams<{ id: string }>()

  // Get current project context
  const navigate = useNavigate()

  // State for storing training data ID
  const [trainingDataId, setTrainingDataId] = useState<string | undefined>("")
  // State for storing AutoML feature mapping data
  const [autoMlData, setAutoMlData] = useState<{
    mapping: Array<CustomFeatureRequest>
    excludedColumns: Array<ExcludedFeatureRequest>
  }>({
    mapping: [],
    excludedColumns: [],
  })

  const [errorMessage, setErrorMessage] = useState<string>("")

  // Mutation for training using pre-built model
  // Includes success/error handling and navigation
  const trainPreBuiltModelMutation = useMutation<
    AxiosResponse,
    AxiosError<baseErrorType>,
    TrainModelUsingPrebuiltModelRequest
  >({
    mutationFn: KonanAPI.trainModelUsingPrebuiltModel,
    onSuccess: (res) => {
      setTimeout(() => {
        navigate({
          pathname: `/projects/${projectId}/model-training`,
          search: `tab=${mapResponseTypesToTabValues(res?.data?.data?.status)}`,
        })
      }, 500)
    },
    onError: () => {
      NotificationUtils.toast("Training Failed", { severity: "error" })
      setErrorMessage(
        trainPreBuiltModelMutation.error?.response?.data.detail ??
          trainPreBuiltModelMutation.error?.response?.data.name ??
          "",
      )
    },
  })

  // Mutation for training using AutoML model
  // Includes success/error handling and navigation
  const {
    mutateAsync: trainAutoMlMutation,
    error: automlCreationErrors,
    isLoading: isTrainAutoMlLoading,
  } = useMutation<AxiosResponse, AxiosError<baseErrorType>, CreateAutoMlRequest>({
    mutationFn: KonanAPI.trainModelUsingAutoMlModel,
    onSuccess: (res) => {
      setTimeout(() => {
        navigate({
          pathname: `/projects/${projectId}/model-training`,
          search: `tab=${mapResponseTypesToTabValues(res?.data?.status)}`,
        })
      }, 500)
    },
    onError: (res) => {
      NotificationUtils.toast("Training Failed", { severity: "error" })
      setErrorMessage(res.response?.data.detail ?? res.response?.data.name ?? "")
    },
  })

  // Formik instance for AutoML form control
  const formik = useFormik({
    initialValues: {
      name: "",
      target_column: "",
      targetColumnType: "default",
    },
    validationSchema: validationSchema,
    onSubmit: async () => {
      try {
        // Submit AutoML training request
        await trainAutoMlMutation({
          projectId: projectId as string,
          name: formik.values.name !== "" ? formik.values.name : "",
          training_data: !!trainingDataId && trainingDataId !== "sample" ? trainingDataId : "",
          target_column: formik.values.target_column,
          excluded_columns: autoMlData.excludedColumns,
          mapping: autoMlData.mapping,
        })
      } catch (error) {
        console.error(error)
      }
    },
  })

  /**
   * Handles training initiation for both AutoML and pre-built models.
   *
   * @param {string} type - Type of training to initiate ("auto-ml" | "pre-built").
   */
  const handleTrainClick = async (type: "auto-ml" | "pre-built"): Promise<void> => {
    if (type === "pre-built") {
      await trainPreBuiltModelMutation.mutateAsync({
        projectId: projectId as string,
        modelName: formik.values.name !== "" ? formik.values.name : undefined,
        trainingDataId: trainingDataId !== "" && trainingDataId !== "sample" ? trainingDataId : undefined,
      })
    } else if (type === "auto-ml") {
      formik.submitForm()
    }
  }

  return (
    <FormikProvider value={formik}>
      <AutoMlModelCreationDialog
        isBtnLoading={isTrainAutoMlLoading}
        open={isOpen}
        projectId={projectId as string}
        trainingDataId={trainingDataId}
        handleTrainClick={(e: "auto-ml" | "pre-built") => handleTrainClick(e)}
        setAutoMlData={setAutoMlData}
        automlErrors={automlCreationErrors}
        setDialog={setIsOpen}
        setTrainingDataId={setTrainingDataId}
        errorMessage={errorMessage}
      />
    </FormikProvider>
  )
}
